import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { upgradeCardInventory } from '../api/cards';
import { fetchWallet } from '../api/wallet';
import GLOBALS from '../config/globals';
import { cardShape } from '../propTypes/cardPropTypes';
import Modal from './Modal';
import {
    getEffectiveCardStatsFromUpgrades,
    getUpgradeEffectText,
    getUpgradeLevel,
    getUpgradeOptionsForCardType,
    getUpgradePoints,
    parseCardUpgrades,
} from '../utils/cardUpgrades';

const STAT_DESCRIPTIONS = {
    stamina: {
        batter: 'Increases how many games per day this batter can play before getting fatigued.',
        pitcher: 'Increases how many batters this pitcher can face before fatigue kicks in.',
    },
    speed: 'Boosts base running speed, improving steal success and extra-base advancement.',
    contact: 'Chance to reroll a strikeout, keeping the ball in play.',
    power: 'Chance to reroll a double into a triple or home run on the extra-base table.',
    xbhSuppress: 'Increases the chance of suppressing extra-base hits when the pitcher Hit Down die triggers.',
    singleSuppress: 'Increases the chance of suppressing singles when the pitcher Power Down die triggers.',
    kModifier: 'Increases the chance of converting walks and outs into strikeouts on K dice.',
    fatigueFighter: 'Reduces the base chance of fatigue flipping a favorable pitcher die result.',
    superStar: 'Earns bonus dollars every game this card appears in your lineup. Fans love a star.',
    drugDealer: 'At game end, this card independently rolls for a bonus energy drink.',
};

const getDescription = (key, isBatter) => {
    const desc = STAT_DESCRIPTIONS[key];
    if (typeof desc === 'object') return isBatter ? desc.batter : desc.pitcher;
    return desc || '';
};

const Tooltip = ({ text, children }) => {
    const [show, setShow] = useState(false);

    return (
        <span
            className="card-upgrades__tooltip-wrap"
            onMouseEnter={() => setShow(true)}
            onMouseLeave={() => setShow(false)}
        >
            {children}
            {show && <span className="card-upgrades__tooltip">{text}</span>}
        </span>
    );
};

const CardUpgrades = ({ card, cardInventory, onCardInventoryUpdated }) => {
    const [upgrading, setUpgrading] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [confirmStat, setConfirmStat] = useState(null);
    const [walletBalance, setWalletBalance] = useState(null);

    const isBatter = card?.type === 'Batter';
    const upgradeOptions = useMemo(() => getUpgradeOptionsForCardType(isBatter), [isBatter]);

    const upgrades = useMemo(() => parseCardUpgrades(cardInventory?.upgrades), [cardInventory?.upgrades]);
    const upgradeLevel = useMemo(() => getUpgradeLevel(upgrades, upgradeOptions), [upgrades, upgradeOptions]);

    const nextLevelCost = GLOBALS.CARD_UPGRADE_COST(upgradeLevel);
    const moneyCost = GLOBALS.CARD_UPGRADE_MONEY_COST(upgradeLevel);
    const inventoryExp = cardInventory?.exp || 0;
    const hasEnoughExp = inventoryExp >= nextLevelCost;
    const hasEnoughMoney = walletBalance !== null ? walletBalance >= moneyCost : true;
    const canLevelUp = hasEnoughExp && hasEnoughMoney;

    useEffect(() => {
        if (showModal && walletBalance === null) {
            fetchWallet().then(data => setWalletBalance(data.balance)).catch(() => {});
        }
    }, [showModal, walletBalance]);

    const { effectiveBatterSpeed, effectivePitcherStamina, effectiveBatterFatigueThreshold } =
        getEffectiveCardStatsFromUpgrades({ card, upgrades, globals: GLOBALS });

    const handleUpgrade = async statKey => {
        if (!cardInventory || !onCardInventoryUpdated || upgrading || !canLevelUp) return;
        setUpgrading(statKey);
        setConfirmStat(null);
        try {
            const result = await upgradeCardInventory(cardInventory.inventoryID, statKey);
            if (result.walletBalance !== undefined) setWalletBalance(result.walletBalance);
            onCardInventoryUpdated({
                ...(cardInventory || {}),
                ...(result || {}),
            });
            toast.success('Upgrade applied');
        } catch (err) {
            const message =
                err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Upgrade failed';
            toast.error(message);
        } finally {
            setUpgrading(null);
        }
    };

    if (!cardInventory) return null;

    const expDisplay = `${inventoryExp}/${nextLevelCost} EXP · $${moneyCost}`;

    const confirmLabel = confirmStat ? upgradeOptions.find(o => o.key === confirmStat)?.label : null;

    return (
        <div className="card-upgrades">
            <div className="card-upgrades__exp-line" onClick={() => setShowModal(true)} role="button" tabIndex={0}>
                <span className="card-upgrades__exp-text">{expDisplay}</span>
                {upgradeLevel > 0 && <span className="card-upgrades__level-badge">Lvl {upgradeLevel}</span>}
                {canLevelUp && (
                    <button type="button" className="card-upgrades__levelup-link" onClick={() => setShowModal(true)}>
                        LEVEL UP
                    </button>
                )}
                {!canLevelUp && upgradeLevel > 0 && (
                    <button type="button" className="card-upgrades__view-link" onClick={() => setShowModal(true)}>
                        View Upgrades
                    </button>
                )}
            </div>

            {showModal && (
                <Modal
                    onClose={() => {
                        setShowModal(false);
                        setConfirmStat(null);
                    }}
                    title="Card Upgrades"
                    className="card-upgrades__modal-wrapper"
                >
                    <div className="card-upgrades__modal">
                        <div className="card-upgrades__modal-header">
                            <h3 className="card-upgrades__modal-title">Upgrades</h3>
                        </div>

                        <div className="card-upgrades__modal-status">
                            <span>Level {upgradeLevel}</span>
                            <span>{inventoryExp}/{nextLevelCost} EXP · ${moneyCost}</span>
                            {walletBalance !== null && (
                                <span className={`card-upgrades__modal-balance${!hasEnoughMoney ? ' card-upgrades__modal-balance--low' : ''}`}>
                                    Balance: ${walletBalance}
                                </span>
                            )}
                        </div>

                        <div className="card-upgrades__modal-rows">
                            {upgradeOptions.map(opt => {
                                const points = getUpgradePoints(upgrades, opt.key);
                                const effectText = getUpgradeEffectText({
                                    key: opt.key,
                                    points,
                                    isBatter,
                                    globals: GLOBALS,
                                    effectiveBatterSpeed,
                                    effectivePitcherStamina,
                                    effectiveBatterFatigueThreshold,
                                });
                                const description = getDescription(opt.key, isBatter);

                                return (
                                    <div key={opt.key} className="card-upgrades__modal-row">
                                        <div className="card-upgrades__modal-row-left">
                                            <Tooltip text={description}>
                                                <span className="card-upgrades__modal-label">
                                                    {opt.label}
                                                    <span className="card-upgrades__modal-info">i</span>
                                                </span>
                                            </Tooltip>
                                            <span className="card-upgrades__modal-effect">{effectText}</span>
                                        </div>
                                        <div className="card-upgrades__modal-row-right">
                                            <span className="card-upgrades__modal-points">{points}</span>
                                            <Tooltip
                                                text={
                                                    canLevelUp
                                                        ? `Costs ${nextLevelCost} EXP + $${moneyCost}. This is permanent.`
                                                        : `Need ${!hasEnoughExp ? `${nextLevelCost - inventoryExp} more EXP` : ''}${!hasEnoughExp && !hasEnoughMoney ? ' and ' : ''}${!hasEnoughMoney ? `$${moneyCost - (walletBalance ?? 0)} more money` : ''}`
                                                }
                                            >
                                                <button
                                                    type="button"
                                                    className="card-upgrades__modal-upgrade-btn"
                                                    onClick={() => canLevelUp && setConfirmStat(opt.key)}
                                                    disabled={!canLevelUp || !!upgrading}
                                                >
                                                    {upgrading === opt.key ? '...' : '+'}
                                                </button>
                                            </Tooltip>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {confirmStat && (
                            <Modal
                                onClose={() => setConfirmStat(null)}
                                title="Confirm Upgrade"
                                className="card-upgrades__confirm-wrapper"
                            >
                                <div className="card-upgrades__confirm">
                                    <p className="card-upgrades__confirm-text">
                                        Upgrade <strong>{confirmLabel}</strong> for {nextLevelCost} EXP and ${moneyCost}?
                                    </p>
                                    <p className="card-upgrades__confirm-warning">
                                        This is permanent and cannot be undone.
                                    </p>
                                    <div className="card-upgrades__confirm-actions">
                                        <button
                                            type="button"
                                            className="card-upgrades__confirm-yes"
                                            onClick={() => handleUpgrade(confirmStat)}
                                            disabled={!!upgrading}
                                        >
                                            {upgrading ? 'Upgrading...' : 'Confirm'}
                                        </button>
                                    </div>
                                </div>
                            </Modal>
                        )}

                    </div>
                </Modal>
            )}
        </div>
    );
};

CardUpgrades.propTypes = {
    card: cardShape,
    cardInventory: PropTypes.shape({
        inventoryID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        exp: PropTypes.number,
        upgrades: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    }),
    onCardInventoryUpdated: PropTypes.func,
};

export default CardUpgrades;
